import { Box, Button, Checkbox, Link, MenuItem, Select, TableCell as MuiTableCell, TableRow, Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import moment from 'moment-timezone';
import React, { useCallback } from 'react';
import { DisplayNameReviewStatus, IDisplayName, IDisplayNameReview } from '../../../services/types';
import CommentUser from '../Comment/CommentUser';
import useStyles from './DisplayName.styles';

interface IProps {
  isReported: boolean;
  displayName: IDisplayName;
  isChecked: boolean;
  disableReviewStatusChange: boolean;
  onCheckboxChange: (displayName: IDisplayName) => void;
  onReviewStatusChange: (displayNameReview: IDisplayNameReview, status: DisplayNameReviewStatus) => void;
  onChangeHistoryClick: (displayName: IDisplayName) => void;
  onInternalNoteClick: (displayName: IDisplayName) => void;
}

const TableCell = withStyles({
  root: {
    verticalAlign: 'top',
    paddingTop: 16,
    paddingBottom: 16,
  },
})(MuiTableCell);

const DisplayNameTableRow = (props: IProps): JSX.Element => {
  const classes = useStyles();

  const {
    isReported,
    displayName,
    isChecked,
    disableReviewStatusChange,
    onCheckboxChange,
    onReviewStatusChange,
    onChangeHistoryClick,
    onInternalNoteClick,
  } = props;

  const handleCheckboxChange = useCallback(() => {
    onCheckboxChange(displayName);
  }, [displayName, onCheckboxChange]);

  const handleReviewStatusChange = useCallback(
    e => {
      if (!displayName.review) {
        return;
      }

      onReviewStatusChange(displayName.review, e.target.value as DisplayNameReviewStatus);
    },
    [displayName, onReviewStatusChange],
  );

  const handleChangeHistoryClick = useCallback(() => {
    onChangeHistoryClick(displayName);
  }, [displayName, onChangeHistoryClick]);

  const handleInternalNoteClick = useCallback(() => {
    onInternalNoteClick(displayName);
  }, [displayName, onInternalNoteClick]);

  return (
    <TableRow key={displayName.id} hover>
      <TableCell padding="checkbox">
        <Checkbox checked={isChecked} color="primary" onChange={handleCheckboxChange} />
      </TableCell>
      <TableCell padding="checkbox">
        <Box className={classes.tableCell80}>
          <Typography className={classes.contentId}>{displayName.id}</Typography>
          <Typography className={classes.contentDesc2}>
            {moment(isReported ? displayName.lastReportedAt : displayName.createdAt).format('ll HH:mm:ss')}
          </Typography>
        </Box>
      </TableCell>
      <TableCell className={classes.tableCell80} padding="checkbox">
        <CommentUser
          userId={displayName.userId}
          displayName={displayName.user?.isWriter ? displayName.displayName : undefined}
          username={displayName.user?.username}
          isWriter={displayName.user?.isWriter || false}
          isDeletedUser={!displayName.user}
        />
      </TableCell>
      <TableCell className={classes.tabelCellContent}>
        <Box>
          <Typography variant="body1">{displayName.displayName}</Typography>
        </Box>
      </TableCell>
      <TableCell align="center">
        {/* Change History */}
        <Link component="div" className={classes.contentReportedLink} onClick={handleChangeHistoryClick}>
          {displayName.changedCount}
        </Link>
      </TableCell>
      <TableCell>
        <Box className={classes.tableCell120}>
          <Select
            disableUnderline={true}
            value={displayName.review?.status}
            onChange={handleReviewStatusChange}
            inputProps={{
              className: cx({
                [classes.contentReviewStatus]: true,
                [classes.contentApproved]: displayName.review?.status === DisplayNameReviewStatus.Approved,
                [classes.contentEmphasize]: displayName.review?.status === DisplayNameReviewStatus.Blocked,
              }),
            }}
            classes={{
              icon: classes.contentSelectIcon,
            }}
            disabled={disableReviewStatusChange}
          >
            <MenuItem value={DisplayNameReviewStatus.NotReviewed}>Not reviewed</MenuItem>
            <MenuItem value={DisplayNameReviewStatus.Approved}>Approved</MenuItem>
            <MenuItem value={DisplayNameReviewStatus.Pending}>Pending</MenuItem>
            <MenuItem value={DisplayNameReviewStatus.Blocked} className={classes.contentEmphasize}>
              Blocked
            </MenuItem>
          </Select>
          {displayName.review?.status !== DisplayNameReviewStatus.NotReviewed && !!displayName.review?.createdAt && (
            <Typography className={classes.contentDesc2}>
              {`${moment(displayName.review?.createdAt).format('ll HH:mm:ss')} (${displayName.review.adminUsername})`}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell align="center">
        {displayName.reportedCount > 0 && <Typography className={classes.contentReportedCount}>{displayName.reportedCount}</Typography>}
      </TableCell>
      <TableCell>
        <Button
          size="small"
          classes={{
            root: cx({
              [classes.contentNotePlaceholder]: true,
              [classes.contentNoteHasContent]: Boolean(displayName.review?.note),
            }),
          }}
          onClick={handleInternalNoteClick}
        >
          {Boolean(displayName.review?.note) ? displayName.review?.note : 'Enter details'}
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default React.memo(DisplayNameTableRow);
